import { Guid } from "./guid";
import { ByteOrByteArray, encode, Writer } from "./writer";


export interface ProtocolPayload
{
	name : string;
	type : string;
	payload? : ProtocolPayload[];
	condition? : string;
}

export interface ProtocolMessage
{
	name : string;
	code : number;
	payload? : ProtocolPayload[];
}

export interface ProtocolConfiguration
{
	client : ProtocolMessage[];
	channel : ProtocolMessage[];
	entity : ProtocolMessage[];
	avatar : ProtocolMessage[];
}

export interface MessageData
{
	code : number;
	time : number;
	emitter : Guid;
	data : Uint8Array;
}


const encoder = new TextEncoder(), decoder = new TextDecoder();


export type PayloadSerializer = (value : any, message : ByteOrByteArray[] ) => number;
export interface DeserializerContext
{
	message : Uint8Array;
};
export type PayloadDeserializer = (context : DeserializerContext, into ? : any ) => any;


export function buildDeserializer(type : string, payload? : ProtocolPayload[] ) : PayloadDeserializer
{
	if(type in primitiveDeserializers)
		return (primitiveDeserializers as any)[type];

	if(type.endsWith("[]"))
	{
		const itemDeserialize = buildDeserializer(type.slice(0, type.length - 2), payload);
		return (context, into) =>
		{
			const result : any[] = [];
			for( let length : number = primitiveDeserializers.Byte(context); --length >= 0; )
				result.push(itemDeserialize(context));
			if(Array.isArray(into))
			{
				into.splice(0, into.length, ...result);
				return into;
			}
			if(typeof into === "function")
				return into(...result);
			return result;
		}
	}


	if(type == "Object")
	{
		const fields = ( payload ?? [] ).map( f => {
			
			const fd = buildDeserializer( f.type, f.payload ), n = f.name;
			
			const d = (context : DeserializerContext, result : any) => {
				const into = result[n];
				const r = fd(context, into);
				if(r !== into)
					result[n] = r;
			};
			
			const c = f.condition;
			if(!c) return d;

			return (context : DeserializerContext, result : any) =>
			{
				const evaluator = (context as any)[c] as Function;
				if(typeof evaluator === "function")
					if(!evaluator.call(context, result))
						return;
				return d(context, result);
			};
		});

		return (context, into) =>
		{
			const result = (typeof into === "object") ? into : {};
			for(let f of fields)
				f(context, result);
			return result;
		};
	}

	throw { type, payload };
};


interface PrimitiveTypes<T>
{
	Byte : T;
	Boolean : T;
	Number : T;
	UInt32 : T;
	Guid : T;
	String : T;
	Vector3 : T;
	Quaternion : T;
}

const primitiveSerializers : PrimitiveTypes< PayloadSerializer > = 
{
	Byte : (value, message) =>
	{
		value ??= 0;
		if( typeof value !== "number" )
			value = parseInt(String(value));
		if(value < 0) message.push(0);
		else if(value > 255) message.push(255);
		else message.push(Math.floor(value));
		return 1;
	},
	Boolean : (value, message) =>
	{
		message.push( Boolean(value) ? 1 : 0 );
		return 1;
	},
	Number : (value, message) =>
	{
		value ??= 0;
		if( typeof value !== "number" )
			value = parseFloat(String(value));
		message.push(Writer.writeFloat(value));
		return 4;
	},
	UInt32 : (value, message) =>
	{
		value ??= 0;
		if( typeof value !== "number" )
			value = parseInt(String(value));
		message.push(Writer.writeUint32(Math.floor(value)));
		return 4;
	},
	Guid : (value, message) =>
	{
		if( !(value instanceof Guid) )
			value = Guid.empty;
		message.push( (value as Guid).asBytes() );
		return 16;
	},
	String : (value, message) =>
	{
		if( typeof value !== "string" )
			value = String(value ?? "");
		const bytes = encoder.encode(value);
		message.push(bytes, 0);
		return 1 + bytes.length;
	},
	Vector3 : (value, message) =>
	{
		message.push(Writer.writeVector3(value ?? { x:0,y:0,z:0 }));
		return 12;
	},
	Quaternion : (value, message) =>
	{
		message.push(Writer.writeQuaternion(value ?? { x:0,y:0,z:0,w:1 }));
		return 16;
	}
};


const primitiveDeserializers : PrimitiveTypes< PayloadDeserializer > = 
{
	Byte : (context) =>
	{
		const result = context.message[0];
		context.message = context.message.subarray(1);
		return result;
	},
	Boolean : (context) =>
	{
		const result = context.message[0];
		context.message = context.message.subarray(1);
		return result != 0;
	},
	Number : (context) =>
	{
		const view = new DataView(context.message.buffer, context.message.byteOffset, 4);
		context.message = context.message.subarray(4);
		return view.getFloat32(0, true);
	},
	UInt32 : (context) =>
	{
		const view = new DataView(context.message.buffer, context.message.byteOffset, 4);
		context.message = context.message.subarray(4);
		return view.getUint32(0, true);
	},
	Guid : (context) =>
	{
		const guid = new Guid(context.message);
		context.message = context.message.subarray(16);
		return guid;
	},
	String : (context) =>
	{
		const data = context.message;
		let n = 0;
		while( (n < data.byteLength) && data[n] ) ++n;
		const value = n ? decoder.decode(data.subarray(0, n)) : "";
		context.message = data.subarray(n+1);
		return value;
	},
	Vector3 : (context, into) =>
	{
		const view = new DataView(context.message.buffer, context.message.byteOffset, 12);
		context.message = context.message.subarray(12);
		const x = view.getFloat32(0, true), y = view.getFloat32(4, true), z = view.getFloat32(8, true);
		if(typeof into === "object")
		{
			if(typeof into.set === "function") into.set(x,y,z);
			else if(typeof into.setValue === "function") into.setValue(x,y,z);
			else (into.x = x, into.y = y, into.z = z);
			return into;
		}
		if(typeof into === "function")
			return into(x,y,z);
		const f = (context as any).Vector3;
		if(typeof f === "function")
			return (f as Function).prototype ? (new f(x,y,z)) : f(x,y,z);
		return {x,y,z};
	},
	Quaternion : (context, into) =>
	{
		const view = new DataView(context.message.buffer, context.message.byteOffset, 16);
		context.message = context.message.subarray(16);
		const x = view.getFloat32(0, true), y = view.getFloat32(4, true), z = view.getFloat32(8, true), w = view.getFloat32(12, true);
		if(typeof into === "object")
		{
			if(typeof into.set === "function") into.set(x,y,z,w);
			else if(typeof into.setValue === "function") into.setValue(x,y,z,w);
			else (into.x = x, into.y = y, into.z = z, into.w = w);
			return into;
		}
		if(typeof into === "function")
			return into(x,y,z);
		const f = (context as any).Vector3;
		if(typeof f === "function")
			return (f as Function).prototype ? (new f(x,y,z,w)) : f(x,y,z,w);
		return {x,y,z,w};
	}
}

/*
const conditionalPrimitiveSerializers : PrimitiveSerializers = 
{
	Byte : (value, message) =>
	{
		if( typeof value !== "number" ) return 0;
		if(value < 0) message.push(0);
		else if(value > 255) message.push(255);
		else message.push(Math.floor(value));
		return 1;
	},
	Boolean : (value, message) =>
	{
		if(value === true) message.push(1);
		else if(value === false) message.push(0);
		else return 0;
		return 1;
	},
	Number : (value, message) =>
	{
		if( typeof value !== "number" ) return 0;
		message.push(Writer.writeFloat(value));
		return 4;
	},
	UInt32 : (value, message) =>
	{
		if( typeof value !== "number" ) return 0;
		message.push(Writer.writeUint32(Math.floor(value)));
		return 4;
	},
	Guid : (value, message) =>
	{
		if( !(value instanceof Guid) ) return 0;
		message.push( (value as Guid).asBytes() );
		return 16;
	},
	String : (value, message) =>
	{
		if(typeof value !== "string") return 0;
		const bytes = encode(value);
		message.push(bytes, 0);
		return 1 + bytes.length;
	},
	Vector3 : (value, message) =>
	{
		if(!value || (typeof value !== "object")) return 0;
		message.push(Writer.writeVector3(value));
		return 12;
	},
	Quaternion : (value, message) =>
	{
		if(!value && (typeof value !== "object")) return 0;
		message.push(Writer.writeQuaternion(value));
		return 16;
	}
};
*/

export function buildSerializer(type:string, payload?:ProtocolPayload[] /*, condition?:string*/ ) : PayloadSerializer
{
	const source = /* condition ? conditionalPrimitiveSerializers :*/ primitiveSerializers;
	if(type in source)
		return (source as any)[type] as PayloadSerializer;


	if(type.endsWith("[]"))
	{
		const itemSerializer = buildSerializer(type.slice(0, type.length-2), payload);
		/* if(condition)
			return (value, message) =>
			{
				if(!Array.isArray(value)) return 0;
				let len = 1;
				message.push(value.length);
				for(let element of value)
					len += itemSerializer(element, message);
				return len;
			}; */
		return (value, message) =>
		{
			value ??= [];
			value = Array.isArray(value) ? value : [value];
			let len = 1;
			message.push(value.length);
			for(let element of value)
				len += itemSerializer(element, message);
			return len;
		};
	}
	
	if(type == "Object")
	{
		const fields = (  payload ?? [] ).map( (field) =>
		{
			const s = buildSerializer(field.type, field.payload /*, field.condition*/ ), n = field.name, c = field.condition;

			if(c)
				return (value : any, message : ByteOrByteArray[] ) => 
				{
					const v = value[n];
					if(v === undefined) return 0;
					return s(v, message);
				};

			return (value : any, message : ByteOrByteArray[] ) => s( value[n], message );
		} );

		/* if(condition) return (value, message) =>
		{
			if(!value || (typeof value !== "object")) return 0;
			let len = 0;
			for(let field of fields)
				len += field(value, message);
			return len;
		}; */

		return (value, message) =>
		{
			value ??= {};
			let len = 0;
			for(let field of fields)
				len += field(value, message);
			return len;
		};
	}

	throw { type, payload };
};
